import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { Button, Icon, Text, theme } from '@sg/garnish';

/**
 * This is *not* the pay with Apple Pay button.
 * Instead it is a button that triggers the Apple Pay payment sheet.
 * The card will be captured and processed in the backend, not in the frontend.
 */
export const ApplePayCheckoutButton = memo(
  (props: ApplePayCheckoutButtonProps) => {
    const { isLoading, disabled, onPress } = props;

    const { formatMessage } = useIntl();
    const checkoutApplePay = formatMessage(messages.checkoutPlaceOrderApplePay);
    const accessibilityLabel = formatMessage(
      messages.checkoutPlaceOrderApplePayA11y,
    );

    return (
      <Button
        palette="black"
        size="large"
        style={[
          styles.applePayButton,
          disabled ? styles.applePayDisabled : null,
        ]}
        isLoading={isLoading}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
        onPress={onPress}
      >
        <Text style={styles.applePayText}>
          {checkoutApplePay}
          <Icon
            name="IconApplePay"
            width={48}
            height={Platform.select({ ios: 30, web: 24 })}
          />
        </Text>
      </Button>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  checkoutPlaceOrderApplePay: {
    defaultMessage: 'Order with ',
    description: 'Checkout | Place Order | Apple Pay',
  },
  checkoutPlaceOrderApplePayA11y: {
    defaultMessage: 'Order with Apple Pay',
    description: 'Checkout | Place Order | Apple Pay | A11y',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  applePayButton: {
    borderRadius: theme.radius['3xLarge'],
  },
  applePayDisabled: {
    backgroundColor: theme.colors.CHARCOAL,
  },
  applePayText: {
    display: 'flex',
    lineHeight: 22,
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ApplePayCheckoutButtonProps = {
  isLoading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
};
